/**
 * Каталог программ обучения (`/training/programs` — canon service-training «Справочник программ и
 * направлений»; training-catalog-v2 — структура каталога заказчицы, ТЗ 26.10.2026, п.4.4 и 4.6).
 * Вкладки: «Программы», «Пакеты», «Разделы» (дерево), «Загрузка и выгрузка» (файл каталога),
 * «Сопоставление» (перенос записей с прежних программ на новые). Содержимое каждой вкладки —
 * отдельный локальный компонент в ./components.
 *
 * Доступ (training-packages-approval; ТЗ 26.10.2026, п.4.4.3): администратор — всё; остальная
 * команда — «Программы» и «Пакеты»: просмотр и добавление (на подтверждение администратору).
 * Вкладка и фильтр живут в URL (`?tab=packages&show=pending`): по ним открывает каталог
 * «колокольчик».
 */
import { useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/app/auth-context'
import { PageHead } from '@/design-system/components/layout/page-head'
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/design-system/components/primitives/tabs'
import { ProgramsTab, type ProgramsShow } from './components/programs'
import { PackagesTab } from './components/packages'
import { DirectionsTab } from './components/directions'
import { CatalogFileTab } from './components/catalog-file'
import { ReplacementsTab } from './components/replacements'

type Tab = 'programs' | 'packages' | 'directions' | 'file' | 'replacements'
const ADMIN_TABS: Tab[] = ['programs', 'packages', 'directions', 'file', 'replacements']
const TEAM_TABS: Tab[] = ['programs', 'packages']
const SHOW_VALUES: ProgramsShow[] = ['active', 'pending', 'rejected', 'archived']

export function TrainingProgramsPage() {
  const { context } = useAuth()
  const isAdmin = context?.user.role === 'admin'
  const accountId = context?.user.id ?? ''
  const tabs = isAdmin ? ADMIN_TABS : TEAM_TABS

  const [searchParams, setSearchParams] = useSearchParams()
  // Каждый переход (в т.ч. повторный из «колокольчика» по тому же адресу) открывает вкладку заново —
  // с фильтром из адреса, даже если его успели сменить вручную.
  const { key: navigationKey } = useLocation()
  const tabParam = searchParams.get('tab') as Tab | null
  const tab: Tab = tabParam && tabs.includes(tabParam) ? tabParam : 'programs'
  const showParam = searchParams.get('show') as ProgramsShow | null
  const initialShow: ProgramsShow =
    showParam && SHOW_VALUES.includes(showParam) ? showParam : 'active'
  const setTab = (next: string) =>
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        p.set('tab', next)
        p.delete('show')
        return p
      },
      { replace: true },
    )

  return (
    <div>
      <PageHead
        title="Каталог программ обучения"
        subtitle={
          isAdmin
            ? 'Справочник программ и пакетов — единый источник для заказов и внешних записей.'
            : 'Справочник программ и пакетов. Добавленное вами появится в каталоге после подтверждения администратором.'
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="programs">Программы</TabsTrigger>
          <TabsTrigger value="packages">Пакеты</TabsTrigger>
          {isAdmin ? (
            <>
              <TabsTrigger value="directions">Разделы</TabsTrigger>
              <TabsTrigger value="file">Загрузка и выгрузка</TabsTrigger>
              <TabsTrigger value="replacements">Сопоставление</TabsTrigger>
            </>
          ) : null}
        </TabsList>
        <TabsContent value="programs">
          <ProgramsTab
            key={navigationKey}
            isAdmin={isAdmin}
            accountId={accountId}
            initialShow={initialShow}
          />
        </TabsContent>
        <TabsContent value="packages">
          <PackagesTab
            key={navigationKey}
            isAdmin={isAdmin}
            accountId={accountId}
            initialShow={initialShow}
          />
        </TabsContent>
        {isAdmin ? (
          <>
            <TabsContent value="directions">
              <DirectionsTab />
            </TabsContent>
            <TabsContent value="file">
              <CatalogFileTab />
            </TabsContent>
            <TabsContent value="replacements">
              <ReplacementsTab />
            </TabsContent>
          </>
        ) : null}
      </Tabs>
    </div>
  )
}
